import React, { useMemo } from 'react';
import Badge from '../../../components/ui/Badge';
import { ReferenceDataGeneralList, type ReferenceInboxColumn } from '../ReferenceDataGeneralList';
import { moduleConfig } from './config';
import { useVessels, type Vessel } from './useData';

const cell = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === '' ? '' : String(value);

const yesNo = (value: boolean) => (value ? 'Yes' : 'No');

const List: React.FC = () => {
  const formatDate = moduleConfig.utils.formatDate;
  const columns = useMemo<ReferenceInboxColumn<Vessel>[]>(
    () => [
      {
        key: 'vesselName',
        header: 'Vessel',
        render: (row) => <span className="font-medium">{row.vesselName}</span>,
      },
      { key: 'vesselAbrv', header: 'Abrv', render: (row) => cell(row.vesselAbrv) },
      { key: 'imoNumber', header: 'IMO', render: (row) => cell(row.imoNumber) },
      { key: 'callSign', header: 'Call sign', render: (row) => cell(row.callSign) },
      { key: 'vesselTypeId', header: 'Type', render: (row) =>
        row.vesselType
          ? row.vesselType.code
            ? `${row.vesselType.code} — ${row.vesselType.name}`
            : row.vesselType.name
          : cell(row.vesselTypeId),
      },
      { key: 'flagId', header: 'Flag', render: (row) =>
        row.flag
          ? row.flag.isoCode2
            ? `${row.flag.isoCode2} — ${row.flag.name}`
            : row.flag.name
          : cell(row.flagId),
      },
      { key: 'ownerId', header: 'Owner', render: (row) =>
        row.owner
          ? row.owner.code
            ? `${row.owner.code} — ${row.owner.name}`
            : row.owner.name
          : cell(row.ownerId),
      },
      {
        key: 'manager',
        header: 'Management company',
        render: (row) =>
          row.manager
            ? row.manager.code
              ? `${row.manager.code} — ${row.manager.name}`
              : row.manager.name
            : cell(row.managerId),
      },
      { key: 'engId', header: 'Engine', render: (row) =>
        row.engineType
          ? row.engineType.code
            ? `${row.engineType.code} — ${row.engineType.name}`
            : row.engineType.name
          : cell(row.engId),
      },
      { key: 'mainEngType', header: 'Main engine', render: (row) => cell(row.mainEngType) },
      { key: 'bhp', header: 'BHP', render: (row) => cell(row.bhp) },
      { key: 'auxEng', header: 'Aux engine', render: (row) => cell(row.auxEng) },
      { key: 'craneTypes', header: 'Cranes', render: (row) => cell(row.craneTypes) },
      { key: 'ecdisType', header: 'ECDIS type', render: (row) =>
        row.ecdisTypeRef
          ? row.ecdisTypeRef.code
            ? `${row.ecdisTypeRef.code} — ${row.ecdisTypeRef.name}`
            : row.ecdisTypeRef.name
          : cell(row.ecdisType),
      },
      { key: 'ecdisModel', header: 'ECDIS model', render: (row) => cell(row.ecdisModel) },
      { key: 'officialNo', header: 'Official no.', render: (row) => cell(row.officialNo) },
      { key: 'grt', header: 'GRT', render: (row) => cell(row.grt) },
      { key: 'nrt', header: 'NRT', render: (row) => cell(row.nrt) },
      { key: 'dwt', header: 'DWT', render: (row) => cell(row.dwt) },
      { key: 'pnumber', header: 'Phones', render: (row) => cell(row.pnumber) },
      { key: 'telex', header: 'Telex', render: (row) => cell(row.telex) },
      { key: 'email', header: 'Email', render: (row) => cell(row.email) },
      {
        key: 'notes',
        header: 'Notes',
        render: (row) => {
          const notes = row.notes?.trim();
          if (!notes) return '';
          return (
            <span className="inline-block max-w-[14rem] truncate" title={notes}>
              {notes}
            </span>
          );
        },
      },
      { key: 'safeEquip', header: 'Safe equip', render: (row) => yesNo(row.safeEquip) },
      { key: 'safeManning', header: 'Safe manning', render: (row) => cell(row.safeManning) },
      {
        key: 'isActive',
        header: 'Active',
        render: (row) => (
          <Badge variant={row.isActive ? 'success' : 'danger'}>{row.isActive ? 'Yes' : 'No'}</Badge>
        ),
      },
      { key: 'isVessel', header: 'Is vessel', render: (row) => yesNo(row.isVessel) },
      { key: 'createdAt', header: 'Created', render: (row) => formatDate(row.createdAt) },
      { key: 'updatedAt', header: 'Updated', render: (row) => formatDate(row.updatedAt) },
    ],
    [formatDate],
  );

  return (
    <ReferenceDataGeneralList
      moduleConfig={moduleConfig}
      useData={useVessels}
      columns={columns}
      defaultSort={{ key: 'vesselName', direction: 'asc' }}
      newLabel="New vessel"
      getRowLabel={(row) => row.vesselName}
    />
  );
};

export default List;
